import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import { SITE_URL } from "@/lib/seo";

export default function ShoppingGuidePage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = t("guide_steps", { returnObjects: true }) as { title: string; desc: string }[];

  const lang = (i18n.language || "en").split("-")[0];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav_home"), item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: t("nav_shop_guide"), item: `${SITE_URL}/shopping-guide` },
    ],
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: t("guide_title"),
    description: t("guide_intro"),
    inLanguage: lang,
    url: `${SITE_URL}/shopping-guide`,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Soft%20minimalist%20shopping%20scene%20with%20folded%20clothes%20and%20shopping%20bags%20on%20a%20pastel%20sage%20green%20background%2C%20clean%20fresh%20editorial%20style%2C%20soft%20natural%20light%2C%20high%20detail&width=1600&height=700&seq=guide-hero-01&orientation=landscape')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/15" />
          <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-24">
            <div className="max-w-[1280px] mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold tracking-wide uppercase">
                {t("guide_eyebrow")}
              </span>
              <h1 className="mt-5 text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight max-w-3xl mx-auto">
                {t("guide_title")}
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed max-w-2xl mx-auto">
                {t("guide_intro")}
              </p>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center">
              {t("guide_steps_title")}
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {steps.map((step, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-base">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-foreground-950 text-base">{step.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category navigation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                {t("categories_title")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700">{t("categories_subtitle")}</p>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((c) => (
                <a
                  key={c.slug}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden cursor-pointer hover:border-primary-300 transition-colors"
                >
                  <div className="w-full h-32 md:h-40 overflow-hidden">
                    <img
                      src={c.image}
                      alt={t(`cats.${c.slug}`)}
                      title={`${t(`cats.${c.slug}`)} - USFans Spreadsheet`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3 md:p-4 flex items-center justify-between gap-2">
                    <span className="text-sm md:text-base font-medium text-foreground-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {t(`cats.${c.slug}`)}
                    </span>
                    <span className="w-6 h-6 shrink-0 flex items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-primary-600">
          <div className="max-w-[1280px] mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">{t("guide_cta_title")}</h2>
            <p className="mt-4 text-sm md:text-base text-background-100 max-w-2xl mx-auto leading-relaxed">
              {t("guide_cta_desc")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-background-50 text-primary-700 font-semibold text-sm cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                {t("final_cta")}
                <i className="ri-arrow-right-up-line" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border border-background-50/60 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                {t("guide_back_home")}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
    </div>
  );
}